import React, {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useHistory, useRouteMatch } from 'react-router';

import { EmployeeModuleTabsLayout } from '~/scenes/PortalViews/Views/EmployeeViews/Modules/components/EmployeeModuleTabs/components/EmployeeModuleTabsLayout';
import { useEmployeeModuleTabContext } from '~/scenes/PortalViews/Views/EmployeeViews/Modules/components/EmployeeModuleTabs/context';
import { EmployeeModuleTabsRouter } from '~/scenes/PortalViews/Views/EmployeeViews/Modules/components/EmployeeModuleTabs/router';
import { EmployeeModuleTabProps } from '~/scenes/PortalViews/Views/EmployeeViews/Modules/types';

export type EmployeeModuleTabsProps<TabPath> = {
  defaultTab: TabPath;
  tabs: EmployeeModuleTabProps<TabPath>[];
  children?: ReactNode;
  enabledTabPaths: TabPath[];
  render: (tab: string) => ReactNode;
};

export const EmployeeModuleTabs = <TabPath,>(
  props: EmployeeModuleTabsProps<TabPath>,
) => {
  const { tabs, defaultTab, enabledTabPaths, render } = props;

  const { setActiveTab, ...tabContext } = useEmployeeModuleTabContext({
    enabledTabPaths,
    defaultTab,
  });

  const [activeTab, setStateActiveTab] = useState(tabContext.previousTab);
  useEffect(() => {
    if (activeTab !== tabContext.activeTab) {
      setStateActiveTab(tabContext.activeTab);
    }
  }, [setStateActiveTab, activeTab, tabContext.activeTab]);

  const history = useHistory();

  const { url } = useRouteMatch();
  const getPath = useCallback((tab: TabPath) => `${url}/${tab}`, [url]);

  const { tabsWithActive } = useMemo(() => {
    const tabsWithActive = !!activeTab
      ? tabs.map((tab) =>
          tab.tabId === activeTab
            ? {
                ...tab,
                active: true,
              }
            : tab,
        )
      : tabs;

    return { tabsWithActive };
  }, [tabs, activeTab]);

  const handleTabChange = useCallback(
    (tabId: TabPath) => {
      if (!!tabId) {
        setActiveTab(tabId);
        history.push(getPath(tabId));
      }
    },
    [setActiveTab, history, getPath],
  );

  return (
    <>
      <EmployeeModuleTabsLayout
        tabs={tabsWithActive}
        onTabChange={handleTabChange}
      >
        <EmployeeModuleTabsRouter
          defaultTab={defaultTab}
          enabledTabPaths={enabledTabPaths}
          render={({ tab }) => render(tab)}
        />
      </EmployeeModuleTabsLayout>
    </>
  );
};
